import React, { useState, useEffect, useRef } from "react";
import { api } from "../../lib/apiClient";
import { Download, Edit3, Plus, Trash2, Upload } from "lucide-react";
import { toast } from "../../components/ui/sonner";
import { useConfirm } from "../../components/ConfirmDialog";
import { Modal } from "../../components/Modal";

export function RepairPartsTab() {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [editing, setEditing] = useState(null);
  const [filterCat, setFilterCat] = useState("All Parts");
  const [importInfo, setImportInfo] = useState("");
  const fileRef = useRef();
  const confirm = useConfirm();

  const load = () => api.get("/repair-parts").then((r) => setItems(r.data));
  useEffect(() => { load(); api.get("/quote-categories").then((r) => setCats(r.data)); }, []);

  const blank = () => ({ name: "", categories: [] });
  const toggleCat = (name) => {
    const has = editing.categories.includes(name);
    setEditing({
      ...editing,
      categories: has ? editing.categories.filter((c) => c !== name) : [...editing.categories, name],
    });
  };
  const save = async () => {
    const name = (editing.name || "").trim();
    if (!name) return toast.error("Name required");
    // Case-insensitive uniqueness check (client side — backend also enforces)
    const lower = name.toLowerCase();
    const clash = items.find((p) => p.name.toLowerCase() === lower && p.id !== editing.id);
    if (clash) return toast.error(`A repair part named "${clash.name}" already exists (case-insensitive).`);
    try {
      if (editing.id) await api.put(`/repair-parts/${editing.id}`, { name, categories: editing.categories });
      else await api.post("/repair-parts", { name, categories: editing.categories });
      setEditing(null); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    }
  };
  const remove = async (id) => {
    const ok = await confirm({
      title: "Delete this part?",
      confirmText: "Delete",
      destructive: true,
    });
    if (!ok) return;
    await api.delete(`/repair-parts/${id}`);
    load();
  };

  const deleteAll = async () => {
    if (items.length === 0) {
      toast.info("There are no repair parts to delete.");
      return;
    }
    const ok = await confirm({
      title: `Delete ALL ${items.length} repair parts?`,
      description: "This cannot be undone — typically used before re-importing a clean master CSV.",
      confirmText: "Delete everything",
      destructive: true,
      requireTypedText: "DELETE ALL",
    });
    if (!ok) return;
    try {
      const { data } = await api.delete("/repair-parts");
      setImportInfo(`Deleted · ${data.deleted} parts removed`);
      toast.success(`Deleted ${data.deleted} parts`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete");
    }
  };

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/repair-parts/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const skip = data.skipped_duplicates ? `, ${data.skipped_duplicates} duplicate${data.skipped_duplicates === 1 ? "" : "s"} skipped` : "";
      setImportInfo(`Imported · ${data.inserted} new, ${data.updated} updated${skip}${data.errors?.length ? `, ${data.errors.length} errors` : ""}`);
    } catch (err) {
      setImportInfo(err?.response?.data?.detail || "Import failed");
    }
    e.target.value = "";
    load();
  };

  const downloadTemplate = () => {
    const csv = 'name,categories\nLimit switch,Roller Shutter Door|Sectional Door\nActivation Sensor,Automatic Door\n';
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "repair-parts-template.csv";
    a.click();
  };

  const downloadList = () => {
    const header = "name,categories\n";
    const escape = (s) => (String(s).includes(",") || String(s).includes('"') ? `"${String(s).replace(/"/g, '""')}"` : String(s));
    const rows = items.map((p) => `${escape(p.name)},${escape((p.categories || []).join("|"))}`).join("\n");
    const blob = new Blob([header + rows + "\n"], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "repair-parts.csv";
    a.click();
  };

  const visibleItems = filterCat === "All Parts"
    ? items
    : items.filter((p) => (p.categories || []).includes(filterCat));

  return (
    <div className="space-y-4" data-testid="parts-tab">
      <div className="text-xs text-slate-500 max-w-2xl">
        Repair parts appear in the Quote Builder when an estimator picks a category. Assign a part to one
        or more categories (e.g. "Limit switch" might apply to both Roller Shutter and Sectional doors).
        These parts are <strong>only</strong> used to find similar past quotes — they don't appear as Material lines.
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={() => setEditing(blank())}
          className="h-9 px-4 bg-brand-blue text-white text-xs uppercase tracking-wider font-semibold inline-flex items-center gap-2 hover:bg-brand-blue-dark"
          data-testid="add-part-button"
        >
          <Plus size={14} /> Add Part
        </button>
        <button onClick={() => fileRef.current?.click()} className="h-9 px-4 border border-slate-900 hover:bg-slate-50 text-xs uppercase tracking-wider font-semibold inline-flex items-center gap-2" data-testid="parts-import-button">
          <Upload size={14} /> Import CSV
        </button>
        <button onClick={downloadList} className="h-9 px-4 border border-slate-300 hover:border-slate-900 text-xs uppercase tracking-wider font-semibold inline-flex items-center gap-2" data-testid="parts-download-button">
          <Download size={14} /> Download CSV
        </button>
        <button onClick={downloadTemplate} className="h-9 px-4 border border-slate-300 hover:border-slate-900 text-xs uppercase tracking-wider font-semibold inline-flex items-center gap-2" data-testid="parts-template-button">
          <Download size={14} /> Template
        </button>
        <button
          onClick={deleteAll}
          className="h-9 px-4 border border-red-300 text-red-600 hover:bg-red-50 hover:border-red-500 text-xs uppercase tracking-wider font-semibold inline-flex items-center gap-2"
          data-testid="parts-delete-all-button"
          title="Wipe every repair part — used before re-importing a clean master list"
        >
          <Trash2 size={14} /> Delete all
        </button>
        <input ref={fileRef} type="file" accept=".csv" onChange={upload} className="hidden" />
        <div className="flex-1" />
        <label className="text-[10px] uppercase tracking-wider text-slate-600 font-semibold">Filter</label>
        <select
          value={filterCat}
          onChange={(e) => setFilterCat(e.target.value)}
          className="h-9 border border-slate-300 px-2 text-sm bg-white"
          data-testid="parts-category-filter"
        >
          <option value="All Parts">All Parts</option>
          {cats.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
        </select>
        {importInfo && <span className="text-xs text-slate-500 ml-2 basis-full">{importInfo}</span>}
      </div>
      <div className="border border-slate-200">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 border-b-2 border-slate-900">
              <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-600">Part</th>
              <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-600">Categories</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {visibleItems.length === 0 && <tr><td colSpan={3} className="px-3 py-6 text-center text-slate-400 text-sm">{items.length === 0 ? "No parts yet." : `No parts in "${filterCat}".`}</td></tr>}
            {visibleItems.map((p) => (
              <tr key={p.id} className="border-b border-slate-200 hover:bg-slate-50">
                <td className="px-3 py-2 font-medium">{p.name}</td>
                <td className="px-3 py-2 text-slate-600">
                  {(p.categories || []).length === 0 ? (
                    <span className="text-slate-400 italic">No categories — won't appear in builder</span>
                  ) : (
                    <div className="flex flex-wrap gap-1.5">
                      {(p.categories || []).map((c) => (
                        <span key={c} className="px-2 py-0.5 rounded-full bg-blue-50 text-brand-blue text-[10px] uppercase tracking-wider font-semibold">{c}</span>
                      ))}
                    </div>
                  )}
                </td>
                <td className="px-3 py-2 text-right space-x-2">
                  <button onClick={() => setEditing({ id: p.id, name: p.name, categories: [...(p.categories || [])] })} className="text-slate-500 hover:text-slate-900"><Edit3 size={14} /></button>
                  <button onClick={() => remove(p.id)} className="text-slate-400 hover:text-red-600"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Modal
        open={!!editing}
        onClose={() => setEditing(null)}
        title={editing?.id ? "Edit Part" : "New Part"}
        size="md"
        footer={editing && (
          <>
            <button onClick={() => setEditing(null)} className="h-9 px-4 rounded-full border border-slate-300 text-xs uppercase tracking-wider font-semibold hover:bg-slate-50">Cancel</button>
            <button onClick={save} className="h-9 px-4 rounded-full bg-brand-blue text-white text-xs uppercase tracking-wider font-semibold hover:bg-brand-blue-dark" data-testid="save-part-button">Save Part</button>
          </>
        )}
      >
        {editing && (
          <div className="space-y-4">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-600 mb-1 block font-semibold">Name</label>
              <input
                value={editing.name}
                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                className="qb-input"
                placeholder="e.g. Push bar"
                autoFocus
                data-testid="part-name-input"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-600 mb-2 block font-semibold">Categories this part applies to</label>
              {cats.length === 0 ? (
                <div className="text-xs text-slate-500">No categories defined yet. Create some in the Categories tab first.</div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {cats.map((c) => {
                    const on = editing.categories.includes(c.name);
                    return (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => toggleCat(c.name)}
                        className={`px-3 py-1 rounded-full text-xs uppercase tracking-wider font-semibold border transition-colors ${
                          on ? "bg-brand-blue text-white border-brand-blue" : "bg-white text-slate-700 border-slate-300 hover:border-brand-blue"
                        }`}
                        data-testid={`toggle-cat-${c.name.replace(/\s+/g, "-").toLowerCase()}`}
                      >{c.name}</button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
